import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import TagCloud from '../components/TagCloud';
import { formatTime } from '../utils/time';
import { getKiLabel, getKiTooltip } from '../utils/recipe';
import useIsMobile from '../hooks/useIsMobile';
import RecipeListMobile from './RecipeList.mobile';
import { useShoppingList } from '../context/ShoppingListContext';
import { useAuth } from '../context/AuthContext';
import { RECIPE_LIST as RL } from '../constants/strings';
import {
  IconNavPlus, IconNavSearch, IconClose, IconNavShopping, IconWand,
  IconUsers, IconClock,
} from '../components/icons';
import './RecipeList.css';

const PAGE_SIZE = 10;

const WARM_CATS  = ['Hauptgerichte','Fleisch & Geflügel','Geflügel','Grill & BBQ','Aus dem Ofen','Aufläufe & Gratins','Festliche Gerichte'];
const COOL_CATS  = ['Suppen & Eintöpfe','Fisch & Meeresfrüchte','Pasta & Nudeln','Reis & Getreide'];
const GREEN_CATS = ['Beilagen','Vegetarisch','Vegan','Salate','Gemüse & Beilagen','Hülsenfrüchte'];
const SWEET_CATS = ['Desserts & Süßes','Frühstück & Brunch','Snacks & Fingerfood','Pizza & Flammkuchen'];

function catColorClass(cat) {
  if (!cat) return 'cat-neutral';
  if (WARM_CATS.includes(cat))  return 'cat-warm';
  if (COOL_CATS.includes(cat))  return 'cat-cool';
  if (GREEN_CATS.includes(cat)) return 'cat-green';
  if (SWEET_CATS.includes(cat)) return 'cat-sweet';
  return 'cat-neutral';
}

const CATEGORY_ICONS = {
  'Hauptgerichte':        '🍽️',
  'Suppen & Eintöpfe':    '🥘',
  'Salate':               '🥗',
  'Pasta & Nudeln':       '🍝',
  'Reis & Getreide':      '🍚',
  'Fleisch & Geflügel':   '🥩',
  'Geflügel':             '🍗',
  'Fisch & Meeresfrüchte':'🐟',
  'Vegetarisch':          '🥦',
  'Vegan':                '🌱',
  'Desserts & Süßes':     '🍰',
  'Frühstück & Brunch':   '🥐',
  'Snacks & Fingerfood':  '🍿',
  'Pizza & Flammkuchen':  '🍕',
  'Aufläufe & Gratins':   '🫕',
  'Grill & BBQ':          '🔥',
  'Aus dem Ofen':         '♨️',
  'Hülsenfrüchte':        '🫘',
  'Gemüse & Beilagen':    '🥕',
  'Festliche Gerichte':   '🎉',
};

function RecipeList() {
  const isMobile = useIsMobile();
  if (isMobile) return <RecipeListMobile />;
  return <RecipeListDesktop />;
}

function RecipeListDesktop() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [activeTags, setActiveTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadError, setLoadError] = useState(false);

  // Kategorien + Tags einmalig laden
  useEffect(() => {
    async function loadFilters() {
      try {
        const [catsData, tagsData] = await Promise.all([
          api.getCategories(),
          api.getTags(true),
        ]);
        setCategories(catsData.categories);
        setAllTags(tagsData.tags);
      } catch (err) {
        console.error(err);
      }
    }
    loadFilters();
  }, []);

  // Rezepte neu laden wenn Kategorie oder Tags sich ändern
  useEffect(() => {
    loadInitial();
  }, [category, activeTags]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInitial() {
    setLoading(true);
    setLoadError(false);
    setOffset(0);
    setRecipes([]);
    try {
      let recipesData;
      if (user) {
        const params = {
          category: category || undefined,
          limit: PAGE_SIZE,
          offset: 0,
          public_only: true,
        };
        if (activeTags.length > 0) params.tags = activeTags.join(',');
        recipesData = await api.getRecipes(params);
      } else {
        recipesData = await api.getPublicRecipes({
          limit: PAGE_SIZE,
          offset: 0,
          category: category || undefined,
        });
      }
      setRecipes(recipesData.recipes);
      setTotal(recipesData.total);
    } catch (err) {
      console.error(err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    const newOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    try {
      let data;
      if (user) {
        const params = {
          category: category || undefined,
          limit: PAGE_SIZE,
          offset: newOffset,
          public_only: true,
        };
        if (activeTags.length > 0) params.tags = activeTags.join(',');
        data = await api.getRecipes(params);
      } else {
        data = await api.getPublicRecipes({
          limit: PAGE_SIZE,
          offset: newOffset,
          category: category || undefined,
        });
      }
      setRecipes(prev => [...prev, ...data.recipes]);
      setOffset(newOffset);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!search.trim()) { loadInitial(); return; }
    setLoading(true);
    try {
      let results;
      if (user) {
        const data = await api.searchRecipes(search);
        results = data.recipes.filter(r => r.is_public);
      } else {
        const data = await api.getPublicRecipes({ search: search.trim(), limit: PAGE_SIZE, offset: 0 });
        results = data.recipes;
      }
      setRecipes(results);
      setTotal(results.length);
      setOffset(9999); // Kein "Mehr laden" bei Suche
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function clearSearch() {
    setSearch('');
    loadInitial();
  }

  function toggleTag(tag) {
    setActiveTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }

  function clearTags() {
    setActiveTags([]);
  }

  const hasMore = recipes.length < total && offset < 9000;

  const subtitle = activeTags.length > 0
    ? RL.subtitleWithTags(total, activeTags.join(', '))
    : RL.subtitle(total);

  return (
    <div className="recipe-list-page">
      {/* ── Header ── */}
      <div className="page-header fade-in">
        <div className="page-header-left">
          <div className="page-title">{RL.title}</div>
          <div className="page-sub">{subtitle}</div>
        </div>
        {user && (
          <Link to="/upload" className="btn btn-primary">
            <IconNavPlus size={16} />
            Rezept scannen
          </Link>
        )}
      </div>

      {/* ── Suchzeile + Sort ── */}
      <form onSubmit={handleSearch} className="filter-bar fade-in">
        <div className="filter-search">
          <IconNavSearch size={16} />
          <input
            type="text"
            placeholder="Rezepte durchsuchen …"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {search && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={clearSearch}><IconClose size={14} /></button>
        )}
        <button type="submit" className="btn btn-primary btn-sm">Suchen</button>
      </form>

      {/* ── Kategorie-Pills ── */}
      <div className="cat-filters fade-in">
        <button
          className={`cf-pill${category === '' ? ' active' : ''}`}
          onClick={() => setCategory('')}
        >
          Alle
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            className={`cf-pill${category === cat ? ' active' : ''}`}
            onClick={() => setCategory(cat)}
          >
            {CATEGORY_ICONS[cat] || ''} {cat}
          </button>
        ))}
      </div>

      {/* Tag-Cloud */}
      <TagCloud
        tags={allTags}
        activeTags={activeTags}
        onToggle={toggleTag}
        onClear={clearTags}
      />

      {/* Ergebnisse */}
      {loading ? (
        <div className="loading-state">
          <div className="loader" />
          <p>{RL.loading}</p>
        </div>
      ) : loadError ? (
        <div className="empty-state fade-in">
          <h3>{RL.errorTitle}</h3>
          <p>{RL.errorText}</p>
        </div>
      ) : recipes.length === 0 ? (
        <div className="empty-state fade-in">
          <h3>{RL.emptyTitle}</h3>
          <p>{RL.emptyText}</p>
          {user && <Link to="/upload" className="btn btn-primary">Rezept scannen</Link>}
        </div>
      ) : (
        <>
          <div className="recipe-rows">
            {recipes.map((recipe, index) => (
              <RecipeCard key={recipe.id} recipe={recipe} index={index} activeTags={activeTags} onTagClick={toggleTag} user={user} />
            ))}
          </div>

          {hasMore && (
            <div className="load-more-wrap">
              <button
                className="btn-load-more"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? RL.loadMoreLoading : RL.loadMore(total - recipes.length)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── RecipeCard ──────────────────────────────────────────────────────────────
function RecipeCard({ recipe, index, activeTags, onTagClick, user }) {
  const tags = Array.isArray(recipe.tags) ? recipe.tags : [];
  const [showCart, setShowCart] = useState(false);
  const [ingredients, setIngredients] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loadingIngs, setLoadingIngs] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);
  const { addItems, removeItem, items } = useShoppingList();
  const popupRef = useRef(null);

  const recipeItems = items.filter(i => i.recipeId === recipe.id && !i.checked);
  const inList = recipeItems.length > 0;

  useEffect(() => {
    if (!showCart) return;
    function handleOutside(e) {
      if (popupRef.current && !popupRef.current.contains(e.target)) setShowCart(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showCart]);

  function handleCartClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (inList) {
      recipeItems.forEach(i => removeItem(i.id));
      return;
    }
    setShowCart(s => !s);
    if (ingredients === null) {
      setLoadingIngs(true);
      api.getRecipe(recipe.id)
        .then(data => {
          const ings = data.recipe?.ingredients || [];
          setIngredients(ings);
          setSelected(ings.map((_, i) => i));
        })
        .catch(() => setIngredients([]))
        .finally(() => setLoadingIngs(false));
    }
  }

  function toggleSelected(i) {
    setSelected(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  }

  function handleAddToList() {
    const toAdd = (ingredients || [])
      .filter((_, i) => selected.includes(i))
      .map(ing => ({
        name: ing.name,
        amount: [ing.amount, ing.unit].filter(Boolean).join(' '),
        recipeId: recipe.id,
      }));
    addItems(toAdd);
    setShowCart(false);
  }

  return (
    <div className="rr-wrap" style={{ animationDelay: `${index * 0.04}s` }}>
      <Link to={`/recipe/${recipe.id}`} className="rr-item fade-in">
        <div className="rr-thumb">
          {recipe.image_path
            ? <img src={recipe.image_path} alt={recipe.title} />
            : (CATEGORY_ICONS[recipe.category] || <IconClock size={24} />)
          }
        </div>

        <div className="rr-main">
          <div className="rr-title">{recipe.title}</div>
          <div className="rr-meta-row">
            {recipe.category && (
              <span className={`cat-badge ${catColorClass(recipe.category)}`}>
                {recipe.category}
              </span>
            )}
            {getKiLabel(recipe.content_source) && (
              <span className="ki-badge-inline" title={getKiTooltip(recipe.content_source)}>
                <IconWand size={12} /> {getKiLabel(recipe.content_source)}
              </span>
            )}
            {recipe.servings && (
              <span className="rr-meta">
                <IconUsers size={13} />
                {recipe.servings} Port.
              </span>
            )}
            {recipe.prep_time > 0 && (
              <span className="rr-meta">
                <IconClock size={13} />
                {formatTime(recipe.prep_time)}
              </span>
            )}
            {tags.length > 0 && tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                className={`tag-chip tag-chip--sm${activeTags.includes(tag) ? ' tag-chip--active' : ''}`}
                onClick={e => { e.preventDefault(); onTagClick(tag); }}
                style={{ cursor: 'pointer' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>

      {user && (
        <button
          className={`recipe-cart-btn${inList ? ' recipe-cart-btn--active' : ''}`}
          onClick={handleCartClick}
          onMouseEnter={() => setBtnHovered(true)}
          onMouseLeave={() => setBtnHovered(false)}
          title={inList ? 'Aus Einkaufsliste entfernen' : 'Zur Einkaufsliste hinzufügen'}
        >
          {inList && btnHovered ? <IconClose size={16} /> : <IconNavShopping size={16} />}
        </button>
      )}

      {user && showCart && (
        <div className="recipe-cart-popup rr-popup" ref={popupRef}>
          <div className="recipe-cart-popup-header">
            <span>Zur Einkaufsliste</span>
            <button className="recipe-cart-popup-close" onClick={() => setShowCart(false)}><IconClose size={14} /></button>
          </div>
          <div className="recipe-cart-popup-body">
            {loadingIngs ? (
              <p className="recipe-cart-loading">Lade Zutaten …</p>
            ) : !ingredients || ingredients.length === 0 ? (
              <p className="recipe-cart-loading">Keine Zutaten gefunden.</p>
            ) : (
              <ul className="recipe-cart-ing-list">
                {ingredients.map((ing, i) => (
                  <li key={i} className="recipe-cart-ing-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={selected.includes(i)}
                        onChange={() => toggleSelected(i)}
                      />
                      <span>
                        {[ing.amount, ing.unit].filter(Boolean).join(' ')}
                        {(ing.amount || ing.unit) ? ' ' : ''}
                        {ing.name}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {!loadingIngs && ingredients && ingredients.length > 0 && (
            <div className="recipe-cart-popup-footer">
              <button className="recipe-cart-cancel" onClick={() => setShowCart(false)}>Abbrechen</button>
              <button
                className="recipe-cart-add"
                onClick={handleAddToList}
                disabled={selected.length === 0}
              >
                Zur Liste ({selected.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RecipeList;
